import React, { useState, useEffect } from 'react';
import './ModernTemplate.css';

const ModernTemplate = ({ data, subdomain }) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://personal-site-saas-api.l5819033.workers.dev';
    const [darkMode, setDarkMode] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    // Initialize activeSection from URL hash or default to home
    useEffect(() => {
        const updateSectionFromHash = () => {
            const hash = window.location.hash.slice(1);
            if (hash && ['home', 'work', 'education', 'skills', 'contact'].includes(hash)) {
                setActiveSection(hash);
            } else {
                setActiveSection('home');
            }
        };

        // Check on mount
        updateSectionFromHash();

        // Listen for hash changes (browser back/forward)
        window.addEventListener('hashchange', updateSectionFromHash);

        return () => window.removeEventListener('hashchange', updateSectionFromHash);
    }, []);
    const [contactFormData, setContactFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Dark mode toggle
    useEffect(() => {
        // Check localStorage or system preference
        const saved = localStorage.getItem('darkMode');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setDarkMode(saved === 'true' || (!saved && prefersDark));
    }, []);

    useEffect(() => {
        document.body.className = darkMode
            ? 'bg-neutral-950 text-neutral-300'
            : 'bg-neutral-100 text-neutral-700';
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    const handleContactChange = (e) => {
        setContactFormData({
            ...contactFormData,
            [e.target.name]: e.target.value
        });
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/site/${subdomain}/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(contactFormData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setSubmitStatus('success');
            setContactFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const hero = data.hero || {};
    const experiences = data.experiences || [];
    const education = data.education || [];
    const competencies = data.competencies || [];
    const tools = data.tools || [];
    const languages = data.languages || [];

    // Format date to "Month Year" format (e.g., "June 2022")
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Return original if invalid

            const months = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];

            const month = months[date.getMonth()];
            const year = date.getFullYear();
            return `${month} ${year}`;
        } catch (error) {
            return dateString; // Return original if error
        }
    };

    const switchSection = (section) => {
        setActiveSection(section);
        // Update URL hash without scrolling
        window.history.pushState(null, '', `#${section}`);
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className={`modern-template ${darkMode ? 'dark' : ''}`}>
            {/* Navigation Bar */}
            <nav className="modern-nav">
                <div className="nav-links">
                    <button
                        className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
                        onClick={() => switchSection('home')}
                    >
                        /home
                    </button>
                    <button
                        className={`nav-link ${activeSection === 'work' ? 'active' : ''}`}
                        onClick={() => switchSection('work')}
                    >
                        /work
                    </button>
                    <button
                        className={`nav-link ${activeSection === 'education' ? 'active' : ''}`}
                        onClick={() => switchSection('education')}
                    >
                        /education
                    </button>
                    <button
                        className={`nav-link ${activeSection === 'skills' ? 'active' : ''}`}
                        onClick={() => switchSection('skills')}
                    >
                        /skills
                    </button>
                    <button
                        className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                        onClick={() => switchSection('contact')}
                    >
                        /contact
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            {activeSection === 'home' && (
                <section id="home" className="modern-hero">
                    <div className="hero-container">
                        <h1 className="hero-title">
                            Hi, I'm {hero.name || 'Your Name'} <span className="hero-emoji">✌️</span>
                        </h1>
                        <p className="hero-description">
                            {hero.description || 'I\'m a developer with a passion for building beautiful and functional applications.'}
                        </p>
                        {(hero.title || hero.current_job) && (
                            <p className="hero-current-job">
                                {hero.title && <>{hero.title}. </>}
                                {hero.current_job && (
                                    <>I currently <strong>/work</strong> as {hero.current_job}</>
                                )}
                            </p>
                        )}
                        <div className="hero-links">
                            {hero.github_url && (
                                <a
                                    href={hero.github_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hero-link"
                                >
                                    GitHub
                                </a>
                            )}
                            {hero.linkedin_url && (
                                <a
                                    href={hero.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hero-link"
                                >
                                    LinkedIn
                                </a>
                            )}
                            {hero.cv_url && (
                                <a
                                    href={hero.cv_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hero-link"
                                >
                                    CV
                                </a>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Work/Experience Section */}
            {activeSection === 'work' && (
                <section id="work" className="modern-section">
                    <div className="section-container">
                        {experiences.length > 0 ? (
                            <div className="experience-list">
                                {experiences.map((exp, i) => (
                                    <div key={i} className="experience-card">
                                        <div className="exp-header">
                                            <h3 className="exp-title">{exp.title || exp.position || 'Role'}</h3>
                                            <span className="exp-company">{exp.company || 'Company'}</span>
                                        </div>
                                        <div className="exp-meta">
                                            <span className="exp-date">
                                                {formatDate(exp.start_date) || ''} {exp.end_date ? `— ${formatDate(exp.end_date)}` : '— Present'}
                                            </span>
                                        </div>
                                        {exp.description && (
                                            <p className="exp-description">{exp.description}</p>
                                        )}
                                        {exp.achievements && exp.achievements.length > 0 && exp.achievements.some(ach => ach.achievement && ach.achievement.trim() !== '') && (
                                            <ul className="exp-achievements">
                                                {exp.achievements.filter(ach => ach.achievement && ach.achievement.trim() !== '').map((ach, idx) => (
                                                    <li key={idx}>{ach.achievement}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="section-empty">No work experience available.</p>
                        )}
                    </div>
                </section>
            )}

            {/* Education Section */}
            {activeSection === 'education' && (
                <section id="education" className="modern-section">
                    <div className="section-container">
                        {education.length > 0 ? (
                            <div className="education-list">
                                {education.map((edu, i) => (
                                    <div key={i} className="education-card">
                                        <div className="edu-header">
                                            <h3 className="edu-degree">{edu.field_of_study || edu.field || 'Field of Study'}</h3>
                                            <span className="edu-institution">{edu.institution || edu.school || 'Institution'}</span>
                                        </div>
                                        {edu.degree && (
                                            <p className="edu-field">{edu.degree}</p>
                                        )}
                                        <div className="edu-meta">
                                            <span className="edu-date">
                                                {formatDate(edu.start_date) || ''} {edu.end_date ? `— ${formatDate(edu.end_date)}` : '— Present'}
                                            </span>
                                        </div>
                                        {edu.description && (
                                            <p className="edu-description">{edu.description}</p>
                                        )}
                                        {edu.achievements && edu.achievements.length > 0 && edu.achievements.some(ach => ach.achievement && ach.achievement.trim() !== '') && (
                                            <ul className="edu-achievements">
                                                {edu.achievements.filter(ach => ach.achievement && ach.achievement.trim() !== '').map((ach, idx) => (
                                                    <li key={idx}>{ach.achievement}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="section-empty">No education information available.</p>
                        )}
                    </div>
                </section>
            )}

            {/* Skills Section */}
            {activeSection === 'skills' && (
                <section id="skills" className="modern-section">
                    <div className="section-container">
                        {(competencies.length > 0 || tools.length > 0 || languages.length > 0) ? (
                            <div className="skills-grid">
                                {competencies.length > 0 && (
                                    <div className="skill-category">
                                        <h3 className="skill-category-title">Competencies</h3>
                                        <div className="skill-tags">
                                            {competencies.map((comp, i) => (
                                                <span key={i} className="skill-tag">{comp.name}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {tools.length > 0 && (
                                    <div className="skill-category">
                                        <h3 className="skill-category-title">Tools & Software</h3>
                                        <div className="skill-tags">
                                            {tools.map((tool, i) => (
                                                <span key={i} className="skill-tag">
                                                    {tool.name}
                                                    {tool.usage_purpose && (
                                                        <span className="skill-usage"> ({tool.usage_purpose})</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {languages.length > 0 && (
                                    <div className="skill-category">
                                        <h3 className="skill-category-title">Languages</h3>
                                        <div className="skill-tags">
                                            {languages.map((lang, i) => (
                                                <span key={i} className="skill-tag">
                                                    {lang.name}
                                                    {lang.level && (
                                                        <span className="skill-level"> ({lang.level})</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="section-empty">No skills information available.</p>
                        )}
                    </div>
                </section>
            )}

            {/* Contact Section */}
            {activeSection === 'contact' && (
                <section id="contact" className="modern-section modern-contact">
                    <div className="section-container">
                        <form onSubmit={handleContactSubmit} className="contact-form">
                            <div className="form-grid">
                                <input
                                    className="form-input"
                                    placeholder="Name"
                                    name="name"
                                    value={contactFormData.name}
                                    onChange={handleContactChange}
                                    required
                                />
                                <input
                                    className="form-input"
                                    type="email"
                                    placeholder="Email"
                                    name="email"
                                    value={contactFormData.email}
                                    onChange={handleContactChange}
                                    required
                                />
                                <input
                                    className="form-input"
                                    placeholder="Subject"
                                    name="subject"
                                    value={contactFormData.subject}
                                    onChange={handleContactChange}
                                    required
                                />
                                <textarea
                                    className="form-textarea"
                                    rows={6}
                                    placeholder="Message"
                                    name="message"
                                    value={contactFormData.message}
                                    onChange={handleContactChange}
                                    required
                                />
                                {submitStatus === 'success' && (
                                    <div className="form-success">
                                        Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapılacaktır.
                                    </div>
                                )}
                                {submitStatus === 'error' && (
                                    <div className="form-error">
                                        Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                                    </div>
                                )}
                                <button
                                    className="form-submit"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Gönderiliyor...' : 'Send Message'}
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            )}
        </div>
    );
};

export default ModernTemplate;

